import { useEffect, useState, useMemo } from 'react'

import { useLazyQuery } from '@hooks/use_lazy_query'

import { USE_USER_PROFILE_QUERY } from './constants'
import {
  getUserProfileFromData,
  isLoading,
  shouldFetchUserProfile
} from './helpers'

/**
 * A hook to fetch the user profile data.
 *
 * This hook implements query deduplication and so if it is used multiple times on the same page only one request will be made.
 *
 * @returns an object with `loading` and `userProfile`. You must check `loading` is `false` before attempting to use `userProfile`
 */
const useUserProfile = () => {
  const [fetchUserProfile, { data, called, loading: queryLoading }] =
    useLazyQuery(USE_USER_PROFILE_QUERY, {
      fetchPolicy: 'cache-first',
      ssr: false
    })

  const [loading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isLoading(called, queryLoading)) {
      setIsLoading(false)
    }
  }, [called, queryLoading])

  useEffect(() => {
    if (shouldFetchUserProfile(called)) {
      fetchUserProfile()
    }
  }, [fetchUserProfile, called])

  const userProfile = useMemo(() => getUserProfileFromData(data), [data])

  if (loading) return { loading: true, userProfile: {} }

  return { loading: false, userProfile }
}

export default useUserProfile
